import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { LscLiveLeagueGame } from '../types';
import TwitchPlayer from './TwitchPlayer';

export type LiveGameEmbedPanelProps = {
  game: LscLiveLeagueGame;
};

function twitchChannelUrl(login: string): string {
  return `https://www.twitch.tv/${login}`;
}

export function formatLiveGameBroadcasterLabel(game: LscLiveLeagueGame): string {
  const memberDisplayName = game.broadcaster_display_name?.trim();
  if (memberDisplayName) return memberDisplayName;
  const displayName = game.twitch_display_name?.trim();
  if (displayName) return displayName;
  const login = game.twitch_login?.trim();
  if (login) return `@${login}`;
  return 'Broadcaster';
}

export function formatLiveGameMatchupLabel(game: LscLiveLeagueGame): string {
  if (game.matchup_display_mode === 'neutral' || game.matchup_label_suppressed) {
    return 'Live verification in progress';
  }
  const home = game.home_team?.trim() || 'Home';
  const away = game.away_team?.trim() || 'Away';
  return `${home} vs ${away}`;
}

const LiveGameEmbedPanel: React.FC<LiveGameEmbedPanelProps> = ({ game }) => {
  const login = game.twitch_login?.trim() ?? '';
  const canEmbed = game.embed_url_ready && Boolean(login);

  if (!canEmbed) {
    return (
      <div
        className="rounded-lg border border-secondary-700 bg-secondary-800/50 p-4 space-y-3"
        data-testid="live-league-game-embed-unavailable"
      >
        <p className="text-sm text-secondary-300">
          League game stream cannot be embedded right now.
        </p>
        {login ? (
          <a
            href={twitchChannelUrl(login)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary-300 hover:text-primary-200 text-sm"
            data-testid="live-league-game-open-on-twitch"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Open on Twitch
          </a>
        ) : null}
      </div>
    );
  }

  const matchupLabel = formatLiveGameMatchupLabel(game);
  const broadcasterLabel = formatLiveGameBroadcasterLabel(game);

  return (
    <div className="space-y-3" data-testid="live-league-game-embed-panel">
      <h3 className="text-sm font-medium text-white">Watch League Game</h3>
      <TwitchPlayer
        mode="live"
        channelLogin={login}
        fallbackUrl={twitchChannelUrl(login)}
        title={`${matchupLabel} — ${broadcasterLabel}`}
        autoplay={false}
      />
    </div>
  );
};

export default LiveGameEmbedPanel;
